import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listTrainings,
  createTraining,
  updateTraining,
  deactivateTraining,
  type TrainingDto,
  type CompetencyImprovement,
} from '../../lib/trainingCatalogue';
import { listCompetencies } from '../../lib/competencies';
import { toast } from 'sonner';
import { GraduationCap, Plus, Pencil, Trash2, BookOpen, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/kinerja/training-catalogue')({
  component: TrainingCataloguePage,
});

function TrainingCataloguePage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <TrainingCatalogueContent />
    </ProtectedRoute>
  );
}

type CompetencyOption = { id: string; code: string; name: string };

const PROVIDER_LABELS = { internal: 'Internal', external: 'External' };
const FORMAT_LABELS = { online: 'Online', offline: 'Offline', blended: 'Blended' };
const FORMAT_COLORS: Record<string, string> = {
  online: 'bg-blue-100 text-blue-700',
  offline: 'bg-purple-100 text-purple-700',
  blended: 'bg-teal-100 text-teal-700',
};

function TrainingCatalogueContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [trainings, setTrainings] = useState<TrainingDto[]>([]);
  const [competencies, setCompetencies] = useState<CompetencyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<TrainingDto | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const emptyForm = {
    code: '',
    name: '',
    description: '',
    provider: 'internal' as 'internal' | 'external',
    format: 'online' as 'online' | 'offline' | 'blended',
    durationHours: '',
    competenciesImproved: [] as Array<CompetencyImprovement & { _key: string }>,
  };
  const [form, setForm] = useState(emptyForm);

  function resetForm(t?: TrainingDto) {
    if (t) {
      setForm({
        code: t.code,
        name: t.name,
        description: t.description ?? '',
        provider: t.provider,
        format: t.format,
        durationHours: t.durationHours?.toString() ?? '',
        competenciesImproved: t.competenciesImproved.map((c) => ({ ...c, _key: crypto.randomUUID() })),
      });
    } else {
      setForm(emptyForm);
    }
  }

  function fetchAll() {
    setLoading(true);
    Promise.all([
      listTrainings(companyId, true),
      listCompetencies({ companyId }),
    ]).then(([tRes, cRes]) => {
      setLoading(false);
      if (tRes.success && tRes.data) setTrainings(tRes.data.trainings);
      if (cRes.success && cRes.data) setCompetencies(cRes.data.competencies ?? []);
    });
  }

  useEffect(() => { fetchAll(); }, [companyId]); // eslint-disable-line

  async function handleSave() {
    if (!form.code.trim() || !form.name.trim()) return;
    setSaving(true);

    const payload = {
      companyId,
      code: form.code,
      name: form.name,
      description: form.description || undefined,
      provider: form.provider,
      format: form.format,
      durationHours: form.durationHours ? Number(form.durationHours) : undefined,
      competenciesImproved: form.competenciesImproved.map(({ _key, ...rest }) => rest),
    };

    const res = editTarget
      ? await updateTraining(editTarget.id, payload)
      : await createTraining(payload);

    setSaving(false);
    if (res.success) {
      toast.success(editTarget ? 'Training updated.' : 'Training added to catalogue.');
      setShowCreate(false);
      setEditTarget(null);
      resetForm();
      fetchAll();
    } else {
      toast.error(res.error ?? 'Save failed');
    }
  }

  async function handleDeactivate(t: TrainingDto) {
    const res = await deactivateTraining(t.id, companyId);
    if (res.success) {
      toast.success(`"${t.name}" deactivated.`);
      fetchAll();
    } else {
      toast.error(res.error ?? 'Failed to deactivate');
    }
  }

  function addCompetencyRow() {
    setForm((f) => ({
      ...f,
      competenciesImproved: [
        ...f.competenciesImproved,
        { _key: crypto.randomUUID(), competencyId: '', competencyCode: '', competencyName: '', levelGainEstimate: 1, targetLevel: null },
      ],
    }));
  }

  function updateCompetencyRow(key: string, field: string, value: string | number | null) {
    setForm((f) => ({
      ...f,
      competenciesImproved: f.competenciesImproved.map((c) =>
        c._key === key ? { ...c, [field]: value } : c,
      ),
    }));
  }

  function removeCompetencyRow(key: string) {
    setForm((f) => ({
      ...f,
      competenciesImproved: f.competenciesImproved.filter((c) => c._key !== key),
    }));
  }

  const active = trainings.filter((t) => t.isActive);
  const inactive = trainings.filter((t) => !t.isActive);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand-50">
            <GraduationCap className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Training Catalogue</h1>
            <p className="text-sm text-gray-500">
              {active.length} active training{active.length !== 1 ? 's' : ''} — AI uses this catalogue when generating IDPs
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowCreate(true); }}>
          <Plus className="w-4 h-4 mr-1.5" />
          Add Training
        </Button>
      </div>

      {/* Training cards */}
      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : active.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">No trainings yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Add trainings with competency mappings so the AI can recommend them in IDPs.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {active.map((t) => (
            <Card key={t.id}>
              <CardContent className="py-4 px-5">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{t.code}</span>
                      <span className="font-medium text-sm text-gray-900">{t.name}</span>
                      <Badge variant="outline" className={`text-xs ${FORMAT_COLORS[t.format] ?? ''}`}>
                        {FORMAT_LABELS[t.format]}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {PROVIDER_LABELS[t.provider]}
                      </Badge>
                      {t.durationHours && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {t.durationHours}h
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>
                    )}
                    {t.competenciesImproved.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {t.competenciesImproved.map((c) => (
                          <span key={c.competencyId} className="text-xs px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 rounded-full">
                            {c.targetLevel != null
                              ? `${c.competencyCode} → ${c.targetLevel}`
                              : `${c.competencyCode} +${c.levelGainEstimate}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => { setEditTarget(t); resetForm(t); setShowCreate(true); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                      onClick={() => handleDeactivate(t)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Inactive section */}
      {inactive.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 mb-2">{inactive.length} inactive training{inactive.length !== 1 ? 's' : ''}</p>
          <div className="grid gap-2 opacity-50">
            {inactive.map((t) => (
              <div key={t.id} className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-gray-50">
                <span className="font-mono text-xs text-gray-400">{t.code}</span>
                <span className="text-sm text-gray-500">{t.name}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto h-6 text-xs"
                  onClick={() => updateTraining(t.id, { companyId, isActive: true }).then(() => fetchAll())}
                >
                  Reactivate
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditTarget(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Training' : 'Add Training'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. LEAD-101"
                  className="mt-1"
                  disabled={!!editTarget}
                />
              </div>
              <div>
                <Label className="text-xs">Duration (hours)</Label>
                <Input
                  type="number"
                  value={form.durationHours}
                  onChange={(e) => setForm((f) => ({ ...f, durationHours: e.target.value }))}
                  placeholder="e.g. 16"
                  className="mt-1"
                  min={0.5}
                  step={0.5}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Training name"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the training…"
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Provider</Label>
                <Select value={form.provider} onValueChange={(v) => setForm((f) => ({ ...f, provider: v as any }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm((f) => ({ ...f, format: v as any }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="blended">Blended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Competencies improved */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Competencies Improved</Label>
                <Button type="button" size="sm" variant="outline" className="h-6 text-xs" onClick={addCompetencyRow}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
              </div>
              {form.competenciesImproved.length === 0 ? (
                <p className="text-xs text-gray-400">No competency mappings yet — add at least one so the AI can recommend this training.</p>
              ) : (
                <div className="space-y-2">
                  {/* Column headers */}
                  <div className="flex items-center gap-2 px-0.5">
                    <span className="flex-1 text-[10px] text-gray-400">Competency</span>
                    <span className="w-16 shrink-0 text-[10px] text-gray-400 text-center" title="Level gain from this training (e.g. +1)">Gain</span>
                    <span className="w-16 shrink-0 text-[10px] text-gray-400 text-center" title="Maximum score this training can bring you to. Leave blank for no cap.">→ Target</span>
                    <span className="w-8 shrink-0" />
                  </div>
                  {form.competenciesImproved.map((c) => {
                    const selected = competencies.find((opt) => opt.id === c.competencyId);
                    return (
                      <div key={c._key} className="flex items-center gap-2">
                        <Select
                          value={c.competencyId}
                          onValueChange={(v) => {
                            const opt = competencies.find((o) => o.id === v);
                            updateCompetencyRow(c._key, 'competencyId', v);
                            if (opt) {
                              updateCompetencyRow(c._key, 'competencyCode', opt.code);
                              updateCompetencyRow(c._key, 'competencyName', opt.name);
                            }
                          }}
                        >
                          <SelectTrigger className="flex-1 h-8 text-xs">
                            <SelectValue placeholder="Select competency">
                              {selected ? `${selected.code} — ${selected.name}` : 'Select competency'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {competencies.map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                {opt.code} — {opt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="w-16 shrink-0">
                          <Input
                            type="number"
                            value={c.levelGainEstimate}
                            onChange={(e) => updateCompetencyRow(c._key, 'levelGainEstimate', Number(e.target.value))}
                            min={0.5}
                            max={5}
                            step={0.5}
                            className="h-8 text-xs text-center"
                            placeholder="+lvl"
                            title="Level gain estimate (e.g. 1 = +1 level)"
                          />
                        </div>
                        <div className="w-16 shrink-0">
                          <Input
                            type="number"
                            value={c.targetLevel ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateCompetencyRow(c._key, 'targetLevel', val === '' ? null : Number(val));
                            }}
                            min={1}
                            max={5}
                            step={0.5}
                            className="h-8 text-xs text-center"
                            placeholder="e.g. 3"
                            title="Target level cap — the max this training can bring you to. Leave blank for no cap."
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-400"
                          onClick={() => removeCompetencyRow(c._key)}
                        >
                          ✕
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditTarget(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.code.trim() || !form.name.trim()}>
              {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Add Training'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
